import React, { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CRow,
  CForm,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CFormCheck,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilArrowBottom } from "@coreui/icons";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { createBulkCountry } from "src/services/country.services";
import toast from "react-hot-toast";

const schema = z.object({
  countries: z.array(z.any()).min(1, "Please upload your country information"),
});

const BulkCountryForm = () => {
  const ref = useRef();
  const defaultErrorMessage = "Something went wrong";

  const {
    register,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      countries: [],
    },
    resolver: zodResolver(schema),
  });

  const onSubmit = (data) => {
    toast
      .promise(
        createBulkCountry(data),
        {
          loading: "Loading",
          success: "Country Created Succesfully",
          error: (err) => {
            return err.response &&
              err.response.data &&
              err.response.data.message
              ? err.response.data.message
              : defaultErrorMessage;
          },
        },
        {
          id: "country",
        }
      )
      .then(async (response) => {
        // console.log("response", response);
        reset();
        ref.current.value = "";
      })
      .catch((error) => console.error(error));
  };

  const onError = (err) => {
    console.error(err);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();

      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const convertedData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
        });
        let serialArray = [];
        let pushData = false;
        convertedData.forEach((row) => {
          // Access the header names and values
          const headerNames = convertedData[0];
          const rowData = {};

          for (let i = 0; i < headerNames.length; i++) {
            const headerName = headerNames[i];
            const value = row[i];
            rowData[headerName] = value;
          }

          if (pushData) {
            serialArray.push(rowData);
          }
          pushData = true;
        });
        setValue("countries", serialArray);
      };

      reader.readAsArrayBuffer(file);
    } else {
      setValue("countries", []);
    }
  };

  const exportToXLSX = () => {
    // Prepare your data to be exported as an array of arrays
    const data = [
      ["name", "code"],
      // Add more rows as needed
    ];

    // Create a new workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(data);

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet 1");

    // Generate the XLSX file buffer
    const xlsxBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    // Create a Blob from the buffer
    const blob = new Blob([xlsxBuffer], { type: "application/octet-stream" });

    // Save the file using FileSaver.js
    saveAs(blob, "Country-Upload.xlsx");
  };

  return (
    <CCard className="mb-4">
      <CCardBody>
        <CRow>
          <CCol sm={5}>
            <h4 id="traffic" className="card-title mb-0">
              Create Bulk Country
            </h4>
          </CCol>
          <CCol sm={7} className="d-none d-md-block"></CCol>
        </CRow>
        <hr />
        <br />
        <CForm onSubmit={handleSubmit(onSubmit, onError)}>
          <CRow>
            <CCol sm={6}>
              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="countries"
                  className="col-sm-3 col-form-label"
                >
                  File:
                </CFormLabel>
                <CCol sm={8}>
                  <CFormInput
                    type="file"
                    accept=".xlsx"
                    onChange={handleFileUpload}
                    id="countries"
                    ref={ref}
                  />
                  {errors.countries?.message && (
                    <p className="error_text">{errors.countries?.message}</p>
                  )}
                </CCol>
              </CRow>
              <CRow>
                <div className="d-flex justify-content-center mt-4">
                  <CButton type="submit" color="primary" className="px-5 py-2">
                    Submit
                  </CButton>
                </div>
              </CRow>
            </CCol>
            <CCol sm={2}></CCol>
            <CCol sm={3}>
              <CCardBody>
                Please click on the download button and copy your country data
                according to the file structure
              </CCardBody>

              <CRow className="mb-3">
                <CButton
                  color="secondary"
                  download
                  style={{ color: "white" }}
                  onClick={exportToXLSX}
                >
                  {/* <CIcon className="my-4 text-white" icon={cilArrowBottom} height={10} /> */}
                  Download Sample
                </CButton>
              </CRow>
            </CCol>
          </CRow>
        </CForm>
      </CCardBody>
    </CCard>
  );
};

export default BulkCountryForm;
